import { Card, ErrorView, InfoPanel, LoadingView, DashStyles as Styles } from '../../components/dashboard/DashboardUI';
import { FeedbackMessage } from '../../components/dashboard/FeedbackMessage';
import { useDashboardData } from '../../components/dashboard/useDashboardData';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { useLobbySettings } from '../../providers/LobbySettingsProvider';
import { useLocale } from '../../providers/LocaleProvider';
import { AccountPanel, OwnerPanel } from './Account';
import { Button, Label, Textbox } from '@app/engine';
import {
  CommonConstants,
  DefaultVoiceOption,
  FormatLocalized,
  GetLocaleDataForAgents,
  GetSupportedCountriesList,
  GetTimezoneList,
  IsPlausibleURL,
  IsValidEmail,
  Logger,
  NormalizeMultiline,
  NormalizeSingleLine,
  VoiceOptionValues
} from '@shared/common';
import { GetUserGeoCountry, GetUserGeoTimezone, NicheContentClient, Telemetry, TenantClient } from '@shared/frontend';
import { ChangeEvent, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import type { IFeedbackDTO } from '../../components/dashboard/FeedbackMessage';
import type { IBusinessHoursWindow, IBusinessProfileDTO, IBusinessService, INicheContentDTO, ITenantDTO, ITenantUserDTO, NotificationChannel } from '@shared/common';

/** File-local constants. */
const localConstants = {
  logContext: 'BusinessProfile',
  daysInWeek: 7, // Sunday (0) through Saturday (6)
  defaultOpenTime: '09:00', // Pre-filled open time when a day is first enabled, 'HH:mm' 24-hour
  defaultCloseTime: '17:00', // Pre-filled close time when a day is first enabled, 'HH:mm' 24-hour
  addressSeparator: ', ',
  emptyNiches: [] as INicheContentDTO[],
  // Google AI Mode search (udm=50 selects AI Mode): the (?) help link prefills a tailored call-instructions prompt and opens in a new tab.
  googleAIURL: 'https://www.google.com/search?udm=50',
  helpGlyph: '?',
  // Primary-subtag separator in BCP-47 codes ('es-MX' -> 'es') and the region-qualifier suffix in locale labels ('Spanish (Americas)') - both used to collapse regional variants in the picker.
  localeSubtagSeparator: '-',
  labelRegionPattern: / \(.+\)$/
} as const;

/** Supported country codes for the country select. */
const supportedCountryCodes = GetSupportedCountriesList().map((c) => c.code);
/** The default country code, derived from the browser or falling back to US. */
const defaultCountryCode = supportedCountryCodes.includes(GetUserGeoCountry()) ? GetUserGeoCountry() : (supportedCountryCodes[0] ?? 'US');
/** The default timezone, derived from the browser or falling back to the system default. */
const defaultTimezone = GetUserGeoTimezone();

/** A single day's editable open/close window in the hours editor (one window per day in this version). */
interface IDayRow {
  dayOfWeek: number;
  enabled: boolean;
  openTime: string;
  closeTime: string;
}

/** A single editable service row in the services editor. Duration is kept as raw text while typing and parsed on save. */
interface IServiceRow {
  name: string;
  description: string;
  duration: string;
}

/**
 * Expands the stored services into editable rows (duration rendered as text for the numeric input).
 *
 * @param services - The stored services.
 * @returns The editable row model.
 */
function ToServiceRows(services: IBusinessService[]): IServiceRow[] {
  return services.map((service) => ({ name: service.name, description: service.description ?? '', duration: service.durationMinutes ? String(service.durationMinutes) : '' }));
}

/**
 * Collapses the editor rows back into storable services: rows without a name are dropped (a blank added row never reaches the prompt), empty descriptions are omitted, and the duration is kept
 * only when it parses to a positive integer within the booking-window cap. Mirrors the server's sanitizeServices so the round-trip is lossless.
 *
 * @param rows - The editable row model.
 * @returns The services to persist.
 */
function FromServiceRows(rows: IServiceRow[]): IBusinessService[] {
  const services: IBusinessService[] = [];
  for (const row of rows) {
    const name = row.name.trim();
    if (name.length === 0) continue;
    const service: IBusinessService = { name };
    const description = row.description.trim();
    if (description.length > 0) service.description = description;
    const duration = Number.parseInt(row.duration, 10);
    if (Number.isInteger(duration) && duration > 0 && duration <= CommonConstants.Validation.MaxDurationMinutes) service.durationMinutes = duration;
    services.push(service);
  }
  return services;
}

/** Editable fields of the business profile form. */
interface IProfileForm {
  name: string;
  legalName: string;
  phoneE164: string;
  contactEmail: string;
  websiteURL: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  countryCode: string;
  greeting: string;
  tone: string;
  agentInstructions: string;
  voiceOption: string;
  notifyChannel: NotificationChannel;
  hours: IBusinessHoursWindow[];
  services: IServiceRow[];
  niche: string;
  timezone: string;
  defaultLocale: string;
  leadsLabel: string;
  bookingsLabel: string;
}

/** The plain-text form keys, narrowed so grid-driven Textbox controls only ever bind string values (excludes the select-backed and structured fields). */
type TextFieldKey =
  | 'name'
  | 'legalName'
  | 'phoneE164'
  | 'contactEmail'
  | 'websiteURL'
  | 'addressLine1'
  | 'addressLine2'
  | 'city'
  | 'region'
  | 'postalCode'
  | 'greeting'
  | 'tone'
  | 'leadsLabel'
  | 'bookingsLabel';

/**
 * Builds the form state from a loaded profile, tenant, and tenant user (or empty defaults).
 *
 * @param profile - The loaded business profile, or null for empty defaults.
 * @param tenant - The loaded tenant, or null for empty defaults.
 * @param user - The loaded tenant user, or null for empty defaults.
 * @returns The populated form state.
 */
function ToForm(profile: IBusinessProfileDTO | null, tenant: ITenantDTO | null, user: ITenantUserDTO | null): IProfileForm {
  return {
    name: tenant?.name ?? '',
    legalName: profile?.legalName ?? '',
    phoneE164: user?.phoneE164 ?? '',
    contactEmail: profile?.contactEmail ?? '',
    websiteURL: profile?.websiteURL ?? '',
    addressLine1: profile?.addressLine1 ?? '',
    addressLine2: profile?.addressLine2 ?? '',
    city: profile?.city ?? '',
    region: profile?.region ?? '',
    postalCode: profile?.postalCode ?? '',
    countryCode: profile?.countryCode || defaultCountryCode,
    greeting: profile?.greeting ?? '',
    tone: profile?.tone ?? '',
    agentInstructions: profile?.agentInstructions ?? '',
    voiceOption: profile?.voiceOption ?? DefaultVoiceOption,
    notifyChannel: profile?.notifyChannel ?? CommonConstants.Database.NotificationChannel.Email,
    hours: profile?.hours ?? [],
    services: ToServiceRows(profile?.services ?? []),
    niche: tenant?.niche ?? '',
    timezone: tenant?.timezone || defaultTimezone,
    defaultLocale: tenant?.defaultLocale ?? '',
    leadsLabel: profile?.customLabels?.[CommonConstants.CustomLabels.Leads] ?? '',
    bookingsLabel: profile?.customLabels?.[CommonConstants.CustomLabels.Bookings] ?? ''
  };
}

/**
 * Expands the stored sparse weekly windows into a fixed Sunday-Saturday row model for editing. Days without a stored window default to disabled with placeholder open/close times.
 *
 * @param hours - The stored weekly opening-hours windows.
 * @returns A seven-element row model, indexed by day of week.
 */
function ToDayRows(hours: IBusinessHoursWindow[]): IDayRow[] {
  return Array.from({ length: localConstants.daysInWeek }, (_unused, dayOfWeek) => {
    const match = hours.find((window) => window.dayOfWeek === dayOfWeek);
    return {
      dayOfWeek,
      enabled: match !== undefined,
      openTime: match?.openTime ?? localConstants.defaultOpenTime,
      closeTime: match?.closeTime ?? localConstants.defaultCloseTime
    };
  });
}

/**
 * Collapses the editor row model back into the stored sparse windows, keeping only enabled days in Sunday-Saturday order.
 *
 * @param rows - The seven-element editor row model.
 * @returns The weekly opening-hours windows to persist.
 */
function FromDayRows(rows: IDayRow[]): IBusinessHoursWindow[] {
  return rows.filter((row) => row.enabled).map((row) => ({ dayOfWeek: row.dayOfWeek, openTime: row.openTime, closeTime: row.closeTime }));
}

/**
 * Help panel: explains how the profile shapes the AI receptionist, what the Agent greeting is, how Notification Channel routes owner alerts, which messages use a fixed channel, and where to get
 * assistance.
 *
 * @returns The profile help panel element.
 */
function ProfileHelpPanel() {
  const { contactEmail } = useLobbySettings();
  return (
    <InfoPanel title={Localized.ProfileHelpTitle}>
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpIntro} />
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpBasics} />
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpServices} />
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpRegion} />
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpLanguage} />
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpAccount} />
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpGreeting} />
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpVoice} />
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpTone} />
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpTerms} />
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpInstructions} />
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpAlerts} />
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpMessages} />
      <Label classNameContainer={Styles.muted} text={Localized.ProfileHelpHours} />
      <Label classNameContainer={Styles.muted} text={FormatLocalized(Localized.AccountHelpContact, contactEmail)} />
    </InfoPanel>
  );
}

/**
 * Business profile screen: view and edit the tenant business details.
 *
 * @returns The business profile element.
 */
export function BusinessProfile() {
  const { tenantID, refreshLabels } = useDashboard();
  const { selectedLang, languages } = useLocale();

  // The Agent-language picker only offers languages the voice agent can actually take calls in (Deepgram-transcribable). `languages` carries the active-UI-language labels; we keep those labels and
  // just narrow the set to the agent-supported codes. Regional variants then collapse to one base-language entry (es-MX/es-ES -> Spanish, pt-BR/pt-PT -> Portuguese): the voice model works on the
  // primary language, so a region split would only confuse the picker.
  const agentLanguages = useMemo(() => {
    const supported = new Set(GetLocaleDataForAgents().map((locale) => locale.Code));
    const seen = new Set<string>();
    return languages
      .filter((lang) => supported.has(lang.code))
      .filter((lang) => {
        const base = lang.code.split(localConstants.localeSubtagSeparator)[0];
        if (seen.has(base)) return false;
        seen.add(base);
        return true;
      })
      .map((lang) => ({ ...lang, label: lang.label.replace(localConstants.labelRegionPattern, '') }));
  }, [languages]);

  const { data: profileData, loading: profileLoading, error: profileError, reload: reloadProfile } = useDashboardData((signal) => TenantClient.GetProfile(signal, tenantID!), [tenantID]);

  const { data: tenantData, loading: tenantLoading, error: tenantError, reload: reloadTenant } = useDashboardData((signal) => TenantClient.GetMyTenant(signal), []);

  const { data: userData, loading: userLoading, error: userError, reload: reloadUser } = useDashboardData((signal) => TenantClient.GetMe(signal), []);

  const {
    data: nichesData,
    loading: nichesLoading,
    error: nichesError,
    reload: reloadNiches
  } = useDashboardData((signal) => NicheContentClient.ListPublicNiches(signal, selectedLang), [selectedLang]);

  const niches = nichesData ?? localConstants.emptyNiches;
  const timezones = useMemo(() => GetTimezoneList(), []);

  const [form, setForm] = useState<IProfileForm>(ToForm(null, null, null));
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<IFeedbackDTO | null>(null);
  const [editingAddress, setEditingAddress] = useState(false);
  const [editingHours, setEditingHours] = useState(false);
  const [editingServices, setEditingServices] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);

  useEffect(() => {
    setForm(ToForm(profileData, tenantData, userData));
  }, [profileData, tenantData, userData]);

  const update = (key: keyof IProfileForm) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  // Controlled-input setter for engine Textbox controls, which emit the raw string value (not a DOM event).
  const updateValue = (key: keyof IProfileForm) => (value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Toggles whether a weekday is open: enabling re-adds its window with placeholder times, disabling drops it.
  const toggleDay = (dayOfWeek: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    const rows = ToDayRows(form.hours).map((row) => (row.dayOfWeek === dayOfWeek ? { ...row, enabled } : row));
    setForm((prev) => ({ ...prev, hours: FromDayRows(rows) }));
  };

  // Sets the open or close time for a weekday window.
  const setDayTime = (dayOfWeek: number, field: 'openTime' | 'closeTime') => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const rows = ToDayRows(form.hours).map((row) => (row.dayOfWeek === dayOfWeek ? { ...row, [field]: value } : row));
    setForm((prev) => ({ ...prev, hours: FromDayRows(rows) }));
  };

  // Updates one field of one service row (name/description via Textbox string setters, duration via the numeric input's raw value).
  const updateServiceField = (index: number, field: keyof IServiceRow) => (value: string) => {
    setForm((prev) => ({ ...prev, services: prev.services.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)) }));
  };

  // Appends a blank service row, capped at the shared per-profile service limit.
  const addService = () => {
    setForm((prev) => (prev.services.length >= CommonConstants.Validation.BusinessServicesMaxCount ? prev : { ...prev, services: [...prev.services, { name: '', description: '', duration: '' }] }));
  };

  // Removes one service row.
  const removeService = (index: number) => {
    setForm((prev) => ({ ...prev, services: prev.services.filter((_unused, rowIndex) => rowIndex !== index) }));
  };

  // Expands the services editor, seeding a first blank row when the list is empty so the operator lands on an editable field.
  const openServicesEditor = () => {
    setForm((prev) => (prev.services.length === 0 ? { ...prev, services: [{ name: '', description: '', duration: '' }] } : prev));
    setEditingServices(true);
  };

  const onSave = async (event?: SyntheticEvent) => {
    event?.preventDefault();
    if (saving) return;
    if (
      form.name.trim().length === 0 ||
      form.legalName.trim().length === 0 ||
      form.niche.length === 0 ||
      form.timezone.length === 0 ||
      form.countryCode.length === 0 ||
      form.greeting.trim().length === 0
    ) {
      setFeedback({ text: Localized.ProfileErrorRequired, type: 'error' });
      return;
    }
    // Client-side format validation mirrors the server's rejects so the user gets an immediate, specific message instead of a generic save error.
    const contactEmail = form.contactEmail.trim();
    if (contactEmail.length > 0 && !IsValidEmail(contactEmail)) {
      setFeedback({ text: Localized.ProfileErrorEmail, type: 'error' });
      return;
    }
    const websiteURL = form.websiteURL.trim();
    if (websiteURL.length > 0 && !IsPlausibleURL(websiteURL)) {
      setFeedback({ text: Localized.ProfileErrorURL, type: 'error' });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      const controller = new AbortController();
      // Only persist custom Leads/Bookings terminology when the selected industry permits it; otherwise leave the stored labels untouched.
      const allowsCustomTerms = niches.find((entry) => entry.niche === form.niche)?.allowCustomTerms ?? false;
      const customLabels = allowsCustomTerms ? { [CommonConstants.CustomLabels.Leads]: form.leadsLabel.trim(), [CommonConstants.CustomLabels.Bookings]: form.bookingsLabel.trim() } : undefined;
      // The save spans two services (profile fields on lobby, business settings on user) with no shared transaction. Both writes are idempotent upserts, and the split error handling keeps a
      // partial save consequence-free: a failed first write persists nothing; a failed second write leaves the profile saved and the form re-synced to the persisted truth for a clean re-save.
      try {
        await TenantClient.UpsertProfile(controller.signal, tenantID!, {
          legalName: form.legalName.trim(),
          contactEmail: form.contactEmail.trim(),
          websiteURL: form.websiteURL.trim(),
          addressLine1: form.addressLine1.trim(),
          addressLine2: form.addressLine2.trim(),
          city: form.city.trim(),
          region: form.region.trim(),
          postalCode: form.postalCode.trim(),
          countryCode: form.countryCode.trim(),
          greeting: NormalizeSingleLine(form.greeting),
          tone: form.tone.trim(),
          agentInstructions: NormalizeMultiline(form.agentInstructions),
          voiceOption: form.voiceOption,
          notifyChannel: form.notifyChannel,
          hours: form.hours,
          services: FromServiceRows(form.services),
          ...(customLabels ? { customLabels } : {})
        });
      } catch (err: unknown) {
        // First write failed: nothing persisted, and the user's edits stay on screen for a corrected retry. The server's reason is shown when one came back.
        Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
        setFeedback({ text: Logger.GetErrorMessage(err) || Localized.AccountSaveError, type: 'error' });
        return;
      }
      try {
        await TenantClient.UpdateMyTenant(controller.signal, {
          name: form.name.trim(),
          niche: form.niche,
          timezone: form.timezone,
          defaultLocale: form.defaultLocale
        });
      } catch (err: unknown) {
        // Partial save: the profile persisted but the business settings did not. Re-sync the form to the persisted state so a re-save is a clean idempotent retry, and say exactly what is unsaved.
        Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
        reloadProfile();
        reloadTenant();
        reloadUser();
        setFeedback({ text: FormatLocalized(Localized.ProfileSavePartial, Logger.GetErrorMessage(err) || Localized.AccountSaveError), type: 'error' });
        return;
      }
      Telemetry.BusinessProfileSaved(profileData !== null);
      setFeedback({ text: Localized.ProfileSaved, type: 'success' });
      reloadProfile();
      reloadTenant();
      reloadUser();
      // Re-resolve the niche/custom Leads/Bookings nouns everywhere (nav, overview, list pages) — each useNicheLabels instance is an isolated fetch, so a shared signal is needed to update them all.
      refreshLabels();
    } finally {
      setSaving(false);
    }
  };

  const loading = profileLoading || tenantLoading || userLoading || nichesLoading;
  const error = profileError ?? tenantError ?? userError ?? nichesError;
  const reloadAll = () => {
    reloadProfile();
    reloadTenant();
    reloadUser();
    reloadNiches();
  };

  if (loading) return <LoadingView />;
  if (error) return <ErrorView onRetry={reloadAll} />;

  // Localized display labels for the generic voice options, built per render so language switches apply (stored values stay English).
  const voiceOptionLabels: Record<string, string> = {
    Female: Localized.ProfileVoiceFemale,
    FemaleBright: Localized.ProfileVoiceFemaleBright,
    FemaleDeep: Localized.ProfileVoiceFemaleDeep,
    Male: Localized.ProfileVoiceMale,
    MaleDeep: Localized.ProfileVoiceMaleDeep,
    MaleBright: Localized.ProfileVoiceMaleBright
  };

  // Localized weekday names, indexed Sunday (0) to Saturday (6) to match IBusinessHoursWindow.dayOfWeek.
  const dayLabels = [Localized.ProfileDay0, Localized.ProfileDay1, Localized.ProfileDay2, Localized.ProfileDay3, Localized.ProfileDay4, Localized.ProfileDay5, Localized.ProfileDay6];

  // Address fields shown inside the collapsible address editor.
  const addressFields: ReadonlyArray<{ key: TextFieldKey; label: string; hint?: string }> = [
    { key: 'addressLine1', label: Localized.ProfileAddressLine1 },
    { key: 'addressLine2', label: Localized.ProfileAddressLine2 },
    { key: 'city', label: Localized.ProfileCity },
    { key: 'region', label: Localized.ProfileRegion, hint: Localized.ProfileRegionHint },
    { key: 'postalCode', label: Localized.ProfilePostal, hint: Localized.ProfilePostalHint }
  ];

  // Collapsed-stub summaries derived from the current form state.
  // The address collapses into discrete, non-wrapping units: street lines, then locality (city/region/postal), then country. Empty parts are dropped so a unit only renders when it has content.
  const addressUnits = [[form.addressLine1, form.addressLine2], [form.city, form.region, form.postalCode], [form.countryCode]]
    .map((parts) =>
      parts
        .map((part) => part.trim())
        .filter((part) => part.length > 0)
        .join(localConstants.addressSeparator)
    )
    .filter((unit) => unit.length > 0);
  const dayRows = ToDayRows(form.hours);
  const openDays = dayRows.filter((row) => row.enabled);
  // When the selected industry permits custom terminology (the broad 'custom' catch-all), the tenant may name their own Leads/Bookings nouns; those fields render only then.
  const allowCustomTerms = niches.find((entry) => entry.niche === form.niche)?.allowCustomTerms ?? false;

  // Collapsed details units: each configured top-of-form value as its own non-wrapping button (empty values are dropped). Always at least the business-name unit, which falls back to the
  // "no profile yet" prompt so a fresh tenant still has something to click.
  const timezoneLabel = (Localized as Record<string, string>)[timezones.find((tz) => tz.iana === form.timezone)?.labelKey ?? ''] ?? form.timezone;
  const languageLabel = languages.find((lang) => lang.code === form.defaultLocale)?.label ?? form.defaultLocale;
  const notifyLabel = form.notifyChannel === CommonConstants.Database.NotificationChannel.SMS ? Localized.ProfileNotifyChannelSMS : Localized.ProfileNotifyChannelEmail;

  // The (?) help link on the call-handling instructions panel: a Google AI Mode search prefilled with a prompt seeded from what the business already told us (name + niche), so the suggested
  // instructions come back tailored. The prompt caps the answer at the field's max length so the result fits without trimming. Falls back to generic wording when the form has nothing yet.
  const instructionsHelpURL = (() => {
    const businessName = form.name.trim() || Localized.ProfileAgentInstructionsHelpNameFallback;
    const nicheLabel = niches.find((entry) => entry.niche === form.niche)?.signupLabel ?? Localized.ProfileAgentInstructionsHelpNicheFallback;
    const prompt = FormatLocalized(Localized.ProfileAgentInstructionsHelpPrompt, businessName, nicheLabel, CommonConstants.Validation.AgentInstructionsMaxLength);
    return `${localConstants.googleAIURL}&q=${encodeURIComponent(prompt)}`;
  })();

  // Conditionally display business name and legal name pills: show only business name if they match (case-insensitive), otherwise show both.
  const businessNameRaw = form.name.trim();
  const legalNameRaw = form.legalName.trim();
  const namePills = businessNameRaw.toLowerCase() === legalNameRaw.toLowerCase() ? [businessNameRaw || Localized.ProfileNone] : [businessNameRaw || Localized.ProfileNone, legalNameRaw];

  const detailUnits = [
    ...namePills,
    form.websiteURL.trim(),
    niches.find((entry) => entry.niche === form.niche)?.signupLabel ?? form.niche,
    timezoneLabel,
    `${Localized.ProfileNotifyChannel}: ${notifyLabel}`,
    `${Localized.AccountLocale}: ${languageLabel}`,
    `${Localized.ProfileVoice}: ${voiceOptionLabels[form.voiceOption] ?? form.voiceOption}`,
    form.tone.trim() ? `${Localized.ProfileTone}: ${form.tone.trim()}` : '',
    allowCustomTerms && form.leadsLabel.trim() ? `${Localized.ProfileLeadsLabel}: ${form.leadsLabel.trim()}` : '',
    allowCustomTerms && form.bookingsLabel.trim() ? `${Localized.ProfileBookingsLabel}: ${form.bookingsLabel.trim()}` : '',
    // Read-only: the plan-derived simultaneous-call cap. Shown so the owner understands why a caller can be asked to call back at peak; changed only by upgrading the plan (or an operator override).
    tenantData ? `${Localized.ProfileConcurrentLimit}: ${tenantData.concurrentLimit}` : ''
  ].filter((unit) => unit.length > 0);

  return (
    // Help prose on top; the business profile form beside the Account/Contact details panels (collapses to one column on tight viewports).
    <div className={Styles.cardStack}>
      <ProfileHelpPanel />
      <div className={`${Styles.cardRow} ${Styles.cardRowTop}`}>
        <Card
          title={Localized.ProfilePanelTitle}
          feedback={<FeedbackMessage feedback={feedback} />}
          actions={
            <div className={Styles.headerActions}>
              {/* Header save action (mirrors the Billing page's Manage billing): type=button because the button sits outside the <form>; it invokes the same onSave as the form submit. */}
              <Button
                text={saving ? Localized.DashSaving : Localized.ProfileSave}
                type="button"
                disabled={saving}
                classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
                classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
                onClickHandler={() => void onSave()}
              />
            </div>
          }
        >
          {profileData === null ? <Label classNameContainer={Styles.muted} text={Localized.ProfileNone} /> : null}
          <form onSubmit={onSave}>
            <div className={Styles.formGrid}>
              {/* Top details (everything above Address) grouped in a nested panel. Collapsed by default (like Address/Hours/Services): each current value renders as a unit button and clicking
                  any of them expands the full field grid. */}
              <div className={`${Styles.field} ${Styles.fieldFull}`}>
                <div className={Styles.subPanel}>
                  <Label classNameContainer={Styles.label} text={Localized.ProfileDetails} title={Localized.ProfileDetails} />
                  {editingDetails ? (
                    <>
                      <div className={Styles.formGrid}>
                        {/* Row 1: Business name, Legal name, Website, Industry — Contact phone/email live on the Contact details panel. */}
                        <div className={Styles.field}>
                          <Label classNameContainer={Styles.label} text={Localized.SharedBusinessName} title={Localized.SharedBusinessName} />
                          <Textbox value={form.name} onChange={updateValue('name')} classNameText={Styles.input} />
                        </div>

                        <div className={Styles.field}>
                          <Label classNameContainer={Styles.label} text={Localized.ProfileLegalName} title={Localized.ProfileLegalNameHint} />
                          <Textbox value={form.legalName} onChange={updateValue('legalName')} classNameText={Styles.input} />
                        </div>

                        <div className={Styles.field}>
                          <Label classNameContainer={Styles.label} text={Localized.ProfileWebsite} title={Localized.ProfileWebsite} />
                          <Textbox value={form.websiteURL} onChange={updateValue('websiteURL')} placeholder={Localized.ProfileWebsitePlaceholder} classNameText={Styles.input} />
                        </div>

                        <div className={Styles.field}>
                          <Label classNameContainer={Styles.label} text={Localized.SharedIndustry} title={Localized.SharedIndustry} />
                          <select className={Styles.input} value={form.niche} onChange={update('niche')}>
                            {form.niche.length > 0 && !niches.some((entry) => entry.niche === form.niche) ? <option value={form.niche}>{form.niche}</option> : null}
                            {niches.map((entry) => (
                              <option key={entry.niche} value={entry.niche}>
                                {entry.signupLabel}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Row 2: Timezone, Notification Channel, Agent language, Agent voice, Agent tone */}
                        <div className={Styles.field}>
                          <Label classNameContainer={Styles.label} text={Localized.AccountTimezone} title={Localized.AccountTimezone} />
                          <select className={Styles.input} value={form.timezone} onChange={update('timezone')}>
                            {form.timezone.length > 0 && !timezones.some((tz) => tz.iana === form.timezone) ? <option value={form.timezone}>{form.timezone}</option> : null}
                            {timezones.map((tz) => (
                              <option key={tz.iana} value={tz.iana}>
                                {(Localized as Record<string, string>)[tz.labelKey] ?? tz.iana}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className={Styles.field}>
                          <Label classNameContainer={Styles.label} text={Localized.ProfileNotifyChannel} title={Localized.ProfileNotifyChannelHint} />
                          <select className={Styles.input} value={form.notifyChannel} onChange={update('notifyChannel')}>
                            <option value={CommonConstants.Database.NotificationChannel.SMS}>{Localized.ProfileNotifyChannelSMS}</option>
                            <option value={CommonConstants.Database.NotificationChannel.Email}>{Localized.ProfileNotifyChannelEmail}</option>
                          </select>
                        </div>

                        <div className={Styles.field}>
                          <Label classNameContainer={Styles.label} text={Localized.AccountLocale} title={Localized.AccountLocaleHint} />
                          <select className={Styles.input} value={form.defaultLocale} onChange={update('defaultLocale')}>
                            {/* Preserve an already-stored value that is no longer an offered agent language (e.g. seeded/legacy) so the select never silently changes it on open. */}
                            {form.defaultLocale.length > 0 && !agentLanguages.some((lang) => lang.code === form.defaultLocale) ? <option value={form.defaultLocale}>{languageLabel}</option> : null}
                            {agentLanguages.map((lang) => (
                              <option key={lang.code} value={lang.code}>
                                {lang.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className={Styles.field}>
                          <Label classNameContainer={Styles.label} text={Localized.ProfileVoice} title={Localized.ProfileVoiceHint} />
                          <select className={Styles.input} value={form.voiceOption} onChange={update('voiceOption')}>
                            {VoiceOptionValues.map((option) => (
                              <option key={option} value={option}>
                                {voiceOptionLabels[option] ?? option}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Agent tone spans the full row so any visible Leads/Bookings term fields wrap onto the next row as a balanced pair. */}
                        <div className={`${Styles.field} ${Styles.fieldFull}`}>
                          <Label classNameContainer={Styles.label} text={Localized.ProfileTone} title={Localized.ProfileToneHint} />
                          <Textbox value={form.tone} onChange={updateValue('tone')} classNameText={Styles.input} />
                        </div>

                        {/* Custom Leads/Bookings terminology — only offered when the selected industry allows custom terms (e.g. the 'Custom' catch-all). */}
                        {allowCustomTerms ? (
                          <>
                            <div className={Styles.field}>
                              <Label classNameContainer={Styles.label} text={Localized.ProfileLeadsLabel} title={Localized.ProfileLeadsLabelHint} />
                              <Textbox
                                value={form.leadsLabel}
                                onChange={updateValue('leadsLabel')}
                                classNameText={Styles.input}
                                placeholder={Localized.ProfileLeadsLabelHint}
                                maxLength={CommonConstants.Validation.ShortTextMaxLength}
                              />
                            </div>

                            <div className={Styles.field}>
                              <Label classNameContainer={Styles.label} text={Localized.ProfileBookingsLabel} title={Localized.ProfileBookingsLabelHint} />
                              <Textbox
                                value={form.bookingsLabel}
                                onChange={updateValue('bookingsLabel')}
                                classNameText={Styles.input}
                                placeholder={Localized.ProfileBookingsLabelHint}
                                maxLength={CommonConstants.Validation.ShortTextMaxLength}
                              />
                            </div>
                          </>
                        ) : null}
                      </div>
                      <div className={Styles.subPanelActions}>
                        <Button type="button" text={Localized.SharedDone} onClickHandler={() => setEditingDetails(false)} classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`} />
                      </div>
                    </>
                  ) : (
                    <div className={Styles.unitStack}>
                      {detailUnits.map((unit) => (
                        <Button
                          key={unit}
                          type="button"
                          text={unit}
                          title={unit}
                          onClickHandler={() => setEditingDetails(true)}
                          classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall} ${Styles.unitButton}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Address editor: a sub-panel whose collapsed state lists the address as discrete, non-wrapping value buttons; clicking any of them expands the full field editor. */}
              <div className={`${Styles.field} ${Styles.fieldFull}`}>
                <div className={Styles.subPanel}>
                  <Label classNameContainer={Styles.label} text={Localized.ProfileAddress} title={Localized.ProfileAddress} />
                  {editingAddress ? (
                    <>
                      <div className={Styles.formGrid}>
                        {addressFields.map((field) => (
                          <div key={field.key} className={Styles.field}>
                            <Label classNameContainer={Styles.label} text={field.label} title={field.hint ?? field.label} />
                            <Textbox value={form[field.key]} onChange={updateValue(field.key)} classNameText={Styles.input} />
                          </div>
                        ))}
                        <div className={Styles.field}>
                          <Label classNameContainer={Styles.label} text={Localized.ProfileCountry} title={Localized.ProfileCountryLocked} />
                          {/* Country is locked once the tenant exists: it sets the Stripe billing currency, which cannot change after signup. The server also ignores any countryCode in the patch. */}
                          <select className={Styles.input} value={form.countryCode} disabled title={Localized.ProfileCountryLocked}>
                            {GetSupportedCountriesList().map((country) => (
                              <option key={country.code} value={country.code}>
                                {(Localized as Record<string, string>)[country.labelKey] ?? country.code}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className={Styles.subPanelActions}>
                        <Button type="button" text={Localized.SharedDone} onClickHandler={() => setEditingAddress(false)} classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`} />
                      </div>
                    </>
                  ) : (
                    <div className={Styles.unitStack}>
                      {addressUnits.length > 0 ? (
                        addressUnits.map((unit) => (
                          <Button
                            key={unit}
                            type="button"
                            text={unit}
                            title={unit}
                            onClickHandler={() => setEditingAddress(true)}
                            classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall} ${Styles.unitButton}`}
                          />
                        ))
                      ) : (
                        <Button
                          type="button"
                          text={Localized.ProfileAddressNone}
                          onClickHandler={() => setEditingAddress(true)}
                          classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall} ${Styles.unitButton}`}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Opening-hours editor: a sub-panel whose collapsed state lists each open day as a non-wrapping value button; clicking any of them expands the per-day editor. */}
              <div className={`${Styles.field} ${Styles.fieldFull}`}>
                <div className={Styles.subPanel}>
                  <Label classNameContainer={Styles.label} text={Localized.ProfileHours} title={Localized.ProfileHours} />
                  {editingHours ? (
                    <>
                      <div className={Styles.hoursEditor}>
                        {dayRows.map((row) => (
                          <div key={row.dayOfWeek} className={Styles.hoursRow}>
                            <div className={Styles.hoursUnit}>
                              <label className={Styles.hoursToggle}>
                                {/* Raw <input>: checkbox needing checked; engine Textbox is a text input with no checked support. */}
                                <input type="checkbox" checked={row.enabled} onChange={toggleDay(row.dayOfWeek)} />
                                {dayLabels[row.dayOfWeek]}
                              </label>
                            </div>
                            <div className={Styles.hoursUnit}>
                              {/* Raw <span>: structural wrapper pairing the caption Label with its time input; engine Label renders text only. */}
                              <span className={Styles.hoursTime}>
                                <Label text={Localized.ProfileHoursOpens} />
                                {/* Raw <input>: time input; engine Textbox would wrap it in a container div and add its own input class, altering this flex row's layout and look. */}
                                <input type="time" className={Styles.timeInput} value={row.openTime} disabled={!row.enabled} onChange={setDayTime(row.dayOfWeek, 'openTime')} />
                              </span>
                              {/* Raw <span>: structural wrapper pairing the caption Label with its time input; engine Label renders text only. */}
                              <span className={Styles.hoursTime}>
                                <Label text={Localized.ProfileHoursCloses} />
                                {/* Raw <input>: time input; engine Textbox would wrap it in a container div and add its own input class, altering this flex row's layout and look. */}
                                <input type="time" className={Styles.timeInput} value={row.closeTime} disabled={!row.enabled} onChange={setDayTime(row.dayOfWeek, 'closeTime')} />
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className={Styles.subPanelActions}>
                        <Button type="button" text={Localized.SharedDone} onClickHandler={() => setEditingHours(false)} classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`} />
                      </div>
                    </>
                  ) : (
                    <div className={Styles.unitStack}>
                      {openDays.length > 0 ? (
                        openDays.map((row) => {
                          const dayUnit = `${dayLabels[row.dayOfWeek]}: ${row.openTime}–${row.closeTime}`;
                          return (
                            <Button
                              key={row.dayOfWeek}
                              type="button"
                              text={dayUnit}
                              title={dayUnit}
                              onClickHandler={() => setEditingHours(true)}
                              classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall} ${Styles.unitButton}`}
                            />
                          );
                        })
                      ) : (
                        <Button
                          type="button"
                          text={Localized.ProfileHoursNone}
                          onClickHandler={() => setEditingHours(true)}
                          classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall} ${Styles.unitButton}`}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Services editor: a sub-panel whose collapsed state lists each service as a non-wrapping value button; clicking any of them expands the row editor. Rows without a name are
                  dropped on save, so a blank added row can never reach the stored profile or the agent's prompt. */}
              <div className={`${Styles.field} ${Styles.fieldFull}`}>
                <div className={Styles.subPanel}>
                  <Label classNameContainer={Styles.label} text={Localized.ProfileServices} title={Localized.ProfileServicesHint} />
                  {editingServices ? (
                    <>
                      <div className={Styles.hoursEditor}>
                        {form.services.map((row, index) => (
                          <div key={index} className={Styles.serviceRow}>
                            <div className={Styles.serviceGrow}>
                              <Textbox
                                value={row.name}
                                onChange={updateServiceField(index, 'name')}
                                placeholder={Localized.ProfileServiceNamePlaceholder}
                                maxLength={CommonConstants.Validation.NameMaxLength}
                                classNameText={Styles.input}
                              />
                            </div>
                            {/* Raw <span>: structural wrapper pairing the caption Label with its duration input; engine Label renders text only. */}
                            <span className={Styles.hoursTime}>
                              <Label text={Localized.ProfileServiceDuration} />
                              {/* Raw <input>: number input needing min/max attributes, which engine Textbox does not forward. */}
                              <input
                                type="number"
                                className={`${Styles.timeInput} ${Styles.durationInput}`}
                                value={row.duration}
                                min={1}
                                max={CommonConstants.Validation.MaxDurationMinutes}
                                onChange={(event) => updateServiceField(index, 'duration')(event.target.value)}
                              />
                            </span>
                            <Button type="button" text={Localized.ProfileServiceRemove} onClickHandler={() => removeService(index)} classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`} />
                            <div className={Styles.serviceDescription}>
                              <Textbox
                                value={row.description}
                                onChange={updateServiceField(index, 'description')}
                                placeholder={Localized.ProfileServiceDescriptionPlaceholder}
                                maxLength={CommonConstants.Validation.ShortTextMaxLength}
                                classNameText={Styles.input}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className={Styles.subPanelActions}>
                        <Button
                          type="button"
                          text={Localized.ProfileServiceAdd}
                          disabled={form.services.length >= CommonConstants.Validation.BusinessServicesMaxCount}
                          onClickHandler={addService}
                          classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
                          classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
                        />
                        <Button type="button" text={Localized.SharedDone} onClickHandler={() => setEditingServices(false)} classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`} />
                      </div>
                    </>
                  ) : (
                    <div className={Styles.unitStack}>
                      {FromServiceRows(form.services).length > 0 ? (
                        FromServiceRows(form.services).map((service, index) => {
                          const serviceUnit = service.durationMinutes ? `${service.name} (${service.durationMinutes} min)` : service.name;
                          return (
                            <Button
                              key={`${service.name}-${index}`}
                              type="button"
                              text={serviceUnit}
                              title={service.description ?? serviceUnit}
                              onClickHandler={openServicesEditor}
                              classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall} ${Styles.unitButton}`}
                            />
                          );
                        })
                      ) : (
                        <Button
                          type="button"
                          text={Localized.ProfileServicesNone}
                          onClickHandler={openServicesEditor}
                          classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall} ${Styles.unitButton}`}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Card>
        <div className={Styles.accountStack}>
          {/* Agent greeting + call-handling instructions sit atop the right column (above Account details). They bind the same form state, so the profile form's Save action still covers them. */}
          <div className={`${Styles.field} ${Styles.fieldFull}`}>
            <div className={Styles.subPanel}>
              <Label classNameContainer={Styles.label} text={Localized.ProfileGreeting} title={Localized.ProfileGreetingHint} />
              <Textbox
                multiline
                value={form.greeting}
                onChange={updateValue('greeting')}
                onBlur={() => setForm((prev) => ({ ...prev, greeting: NormalizeSingleLine(prev.greeting) }))}
                classNameText={`${Styles.textarea} ${Styles.textareaShort}`}
              />
            </div>
          </div>

          {/* Tenant-authored call-handling instructions (the custom call script): folded into the agent's system prompt below the safety/global/niche rules. */}
          <div className={`${Styles.field} ${Styles.fieldFull}`}>
            <div className={Styles.subPanel}>
              <div className={Styles.infoHeader}>
                <Label classNameContainer={Styles.label} text={Localized.ProfileAgentInstructions} title={Localized.ProfileAgentInstructionsHint} />
                {/* Raw <a>: external link opening Google AI Mode in a new tab; engine Button has no href/target support. Reuses the round InfoPanel toggle styling. */}
                <a
                  className={Styles.infoToggle}
                  href={instructionsHelpURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={Localized.ProfileAgentInstructionsHelp}
                  aria-label={Localized.ProfileAgentInstructionsHelp}
                >
                  {localConstants.helpGlyph}
                </a>
              </div>
              <Textbox
                multiline
                value={form.agentInstructions}
                onChange={updateValue('agentInstructions')}
                onBlur={() => setForm((prev) => ({ ...prev, agentInstructions: NormalizeMultiline(prev.agentInstructions) }))}
                placeholder={Localized.ProfileAgentInstructionsHint}
                maxLength={CommonConstants.Validation.AgentInstructionsMaxLength}
                classNameText={`${Styles.textarea}`}
              />
            </div>
          </div>
          <AccountPanel />
          <OwnerPanel />
        </div>
      </div>
    </div>
  );
}
